import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { I18nProvider } from "./context/I18nContext";
import { AuthProvider, AuthCallback, ProtectedRoute, AdminRoute } from "./context/AuthContext";
import SEOHead from "./components/SEOHead";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import RecipesPage from "./pages/RecipesPage";
import RecipeDetailPage from "./pages/RecipeDetailPage";
import RecipeFormPage from "./pages/RecipeFormPage";
import MenusPage from "./pages/MenusPage";
import MenuEditorPage from "./pages/MenuEditorPage";
import TrainingPage from "./pages/TrainingPage";
import ExercisesPage from "./pages/ExercisesPage";
import TrainingPlansPage from "./pages/TrainingPlansPage";
import EvolutionPage from "./pages/EvolutionPage";
import RunTrackerPage from "./pages/RunTrackerPage";
import TrainingPlanDetailPage from "./pages/TrainingPlanDetailPage";
import NutritionPlansPage from "./pages/NutritionPlansPage";
import NutritionPlanDetailPage from "./pages/NutritionPlanDetailPage";
import HistoryPage from "./pages/HistoryPage";
import SettingsPage from "./pages/SettingsPage";
import PricingPage from "./pages/PricingPage";
import AdminPage from "./pages/AdminPage";
import PrivacyPage from "./pages/PrivacyPage";
import ContactPage from "./pages/ContactPage";

// App Router component to handle auth callback detection
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions with auth check
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />

      <Route path="/recipes" element={
        <ProtectedRoute>
          <RecipesPage />
        </ProtectedRoute>
      } />

      <Route path="/recipes/new" element={
        <ProtectedRoute>
          <RecipeFormPage />
        </ProtectedRoute>
      } />

      <Route path="/recipes/:recipeId" element={
        <ProtectedRoute>
          <RecipeDetailPage />
        </ProtectedRoute>
      } />

      <Route path="/recipes/:recipeId/edit" element={
        <ProtectedRoute>
          <RecipeFormPage />
        </ProtectedRoute>
      } />

      <Route path="/menu/new" element={
        <ProtectedRoute>
          <MenuEditorPage />
        </ProtectedRoute>
      } />

      <Route path="/menus" element={
        <ProtectedRoute>
          <MenusPage />
        </ProtectedRoute>
      } />

      <Route path="/training" element={
        <ProtectedRoute>
          <TrainingPage />
        </ProtectedRoute>
      } />

      <Route path="/exercises" element={
        <ProtectedRoute>
          <ExercisesPage />
        </ProtectedRoute>
      } />

      <Route path="/training/plans" element={
        <ProtectedRoute>
          <TrainingPlansPage />
        </ProtectedRoute>
      } />

      <Route path="/training/evolution" element={
        <ProtectedRoute>
          <EvolutionPage />
        </ProtectedRoute>
      } />

      <Route path="/track" element={
        <ProtectedRoute>
          <RunTrackerPage />
        </ProtectedRoute>
      } />

      <Route path="/training/plans/:planId" element={
        <ProtectedRoute>
          <TrainingPlanDetailPage />
        </ProtectedRoute>
      } />

      <Route path="/nutrition/plans" element={
        <ProtectedRoute>
          <NutritionPlansPage />
        </ProtectedRoute>
      } />

      <Route path="/nutrition/plans/:planId" element={
        <ProtectedRoute>
          <NutritionPlanDetailPage />
        </ProtectedRoute>
      } />

      <Route path="/menu/:menuId" element={
        <ProtectedRoute>
          <MenuEditorPage />
        </ProtectedRoute>
      } />

      <Route path="/history" element={
        <ProtectedRoute>
          <HistoryPage />
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminPage />
        </AdminRoute>
      } />

      <Route path="/contact" element={
        <ProtectedRoute>
          <ContactPage />
        </ProtectedRoute>
      } />

      {/* Privacy Policy */}
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Fallback - redirect to home */}
      <Route path="*" element={<LandingPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <SEOHead />
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster
              position="top-center"
              toastOptions={{
                className: 'font-inter',
                style: {
                  borderRadius: '1rem',
                },
              }}
            />
          </AuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </div>
  );
}

export default App;
